import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export function VoiceDictation({ caseId, mode = "global", targetField, fieldLabel, sectionLabels, onApplied }) {
  const [state, setState] = useState("ready"); // ready | listening | processing | transcribed | structuring | review | error
  const [seconds, setSeconds] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [accepted, setAccepted] = useState({});
  const [applying, setApplying] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => {
    clearInterval(timerRef.current);
    try { recorderRef.current?.stream?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
  }, []);

  const reset = () => { setState("ready"); setTranscript(""); setSessionId(null); setUpdates([]); setAccepted({}); };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        await sendAudio(new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" }));
      };
      rec.start();
      recorderRef.current = rec;
      setSeconds(0);
      setState("listening");
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      toast.error("Microphone access denied or unavailable.");
      setState("error");
    }
  };

  const stop = () => {
    clearInterval(timerRef.current);
    try { recorderRef.current?.stop(); } catch { /* ignore */ }
    setState("processing");
  };

  const sendAudio = async (blob) => {
    try {
      const fd = new FormData();
      fd.append("audio", blob, "dictation.webm");
      const { data } = await api.post(`/cases/${caseId}/voice/transcribe`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setTranscript(data.transcript);
      setSessionId(data.session_id);
      setState("transcribed");
    } catch (e) {
      toast.error(errMsg(e));
      setState("error");
    }
  };

  const structure = async () => {
    setState("structuring");
    try {
      const { data } = await api.post(`/cases/${caseId}/voice/structure`, {
        session_id: sessionId, edited_transcript: transcript, mode, target_field: targetField,
      });
      if (data.fallback) {
        toast.error(data.message);
        setState("transcribed");
        return;
      }
      setUpdates(data.updates);
      setAccepted(Object.fromEntries(data.updates.map((u, i) => [i, u.confidence >= 0.7])));
      setState("review");
    } catch (e) {
      toast.error(errMsg(e));
      setState("transcribed");
    }
  };

  const apply = async () => {
    const approvals = updates.filter((_, i) => accepted[i]).map((u) => ({ field: u.field, content: u.content }));
    if (!approvals.length) { toast.error("Accept at least one suggestion to apply."); return; }
    setApplying(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/voice/apply`, { session_id: sessionId, approvals });
      toast.success("Dictation applied — appended to the report draft.");
      onApplied?.(data.sections);
      reset();
    } catch (e) { toast.error(errMsg(e)); } finally { setApplying(false); }
  };

  const editUpdate = (i, v) => setUpdates((us) => us.map((u, j) => (j === i ? { ...u, content: v } : u)));

  if (state === "ready" || state === "error") {
    return (
      <Button type="button" variant="outline" size="sm" onClick={start}
        data-testid={mode === "field" ? `mic-${targetField}` : "mic-global"}
        className="rounded-full border-[#7E9B82]/50 text-[#1E3F20] hover:bg-[#7E9B82]/10 transition-colors">
        <Mic className="h-4 w-4 mr-1.5" /> {mode === "field" ? "Dictate" : "Dictate evaluation notes"}
      </Button>
    );
  }

  return (
    <div className="rounded-xl border border-[#E5E5DF] bg-white p-5 space-y-4" data-testid={mode === "field" ? `voice-panel-${targetField}` : "voice-panel-global"}>
      {state === "listening" && (
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-2 text-sm font-semibold text-[#8C2A2A]" data-testid="voice-listening-indicator">
            <span className="h-2.5 w-2.5 rounded-full bg-[#8C2A2A] animate-pulse" /> Listening… {fmt(seconds)}
          </p>
          <Button type="button" size="sm" onClick={stop} data-testid="voice-stop-btn"
            className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white">
            <Square className="h-3.5 w-3.5 mr-1.5" /> Stop
          </Button>
        </div>
      )}
      {state === "processing" && (
        <p className="flex items-center gap-2 text-sm text-[#5C5C57]" data-testid="voice-processing">
          <Loader2 className="h-4 w-4 animate-spin" /> Processing transcription…
        </p>
      )}
      {state === "transcribed" && (
        <>
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#5C5C57]">Review & edit transcript</p>
          <Textarea rows={4} value={transcript} onChange={(e) => setTranscript(e.target.value)}
            data-testid="voice-transcript-editor" className="input-warm border-[#E5E5DF] text-sm leading-6" />
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={structure} data-testid="voice-structure-btn"
              className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white">
              {mode === "field" ? `Place in ${fieldLabel || "field"}` : "Organize into report fields"}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={reset} data-testid="voice-discard-btn"
              className="rounded-full text-[#5C5C57]">Discard</Button>
          </div>
        </>
      )}
      {state === "structuring" && (
        <p className="flex items-center gap-2 text-sm text-[#5C5C57]" data-testid="voice-structuring">
          <Loader2 className="h-4 w-4 animate-spin" /> Identifying relevant report sections…
        </p>
      )}
      {state === "review" && (
        <>
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#5C5C57]">Proposed placements — review before applying</p>
          <div className="space-y-3">
            {updates.map((u, i) => (
              <div key={i} className={`rounded-lg border p-4 ${u.change_type === "contradiction" ? "border-[#8C2A2A]/40 bg-[#8C2A2A]/5" : "border-[#E5E5DF]"}`}
                data-testid={`voice-suggestion-${i}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <Checkbox checked={!!accepted[i]} onCheckedChange={(v) => setAccepted((a) => ({ ...a, [i]: !!v }))}
                      data-testid={`voice-accept-${i}`} className="mt-0.5" />
                    <span>
                      <span className="text-sm font-semibold">{sectionLabels?.[u.field] || u.field}</span>
                      <span className="ml-2 text-[11px] text-[#5C5C57]">
                        {u.source?.replace(/_/g, " ")} · confidence {Math.round(u.confidence * 100)}%
                        {u.confidence < 0.9 ? " · review recommended" : ""}
                      </span>
                    </span>
                  </label>
                </div>
                {u.change_type === "contradiction" && (
                  <p className="flex items-center gap-1.5 text-xs text-[#8C2A2A] font-semibold mb-2" data-testid={`voice-conflict-${i}`}>
                    <AlertTriangle className="h-3.5 w-3.5" /> Potential conflicting information — please review carefully
                  </p>
                )}
                <Textarea rows={3} value={u.content} onChange={(e) => editUpdate(i, e.target.value)}
                  data-testid={`voice-content-${i}`} className="input-warm border-[#E5E5DF] text-sm leading-6" />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={apply} disabled={applying} data-testid="voice-apply-btn"
              className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white">
              {applying ? "Applying…" : "Apply selected to draft"}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={reset} data-testid="voice-cancel-btn"
              className="rounded-full text-[#5C5C57]">Discard</Button>
          </div>
          <p className="text-[11px] text-[#5C5C57]">Applied text is appended — existing content is never overwritten. The raw transcript is retained in the audit trail; audio is not stored.</p>
        </>
      )}
    </div>
  );
}
